import React, { useState, useEffect } from 'react';
import { Button, Badge, Input, Select } from './shared';
import DatabaseViewer from './DatabaseViewer';
import DatabaseConfigForm from './DatabaseConfigForm';

const ProjectsUnified = () => {
    // TEST_BUILD_MARKER_XYZ123 - If you can find this in the build, changes are working
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddRepoModal, setShowAddRepoModal] = useState(false);
    const [showBugModal, setShowBugModal] = useState(false);
    const [showFeatureModal, setShowFeatureModal] = useState(false);
    const [showEditIssueModal, setShowEditIssueModal] = useState(false);
    const [editingIssue, setEditingIssue] = useState(null);
    const [showDatabaseConfigModal, setShowDatabaseConfigModal] = useState(false);
    const [configuringSubProject, setConfiguringSubProject] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [githubRepos, setGithubRepos] = useState([]);
    const [loadingGithub, setLoadingGithub] = useState(false);
    const [selectedGithubRepos, setSelectedGithubRepos] = useState([]);
    const [repoSearchQuery, setRepoSearchQuery] = useState('');
    const [documentation, setDocumentation] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [issues, setIssues] = useState([]);
    const [availableDomains, setAvailableDomains] = useState([]);
    const [loadingDomains, setLoadingDomains] = useState(false);
    const [availableSentryProjects, setAvailableSentryProjects] = useState([]);
    const [loadingSentryProjects, setLoadingSentryProjects] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        description: '',
        status: 'planning',
        priority: 'medium',
        domain: '',
        sentry_project_ids: []
    });

    // Form states
    const [projectForm, setProjectForm] = useState({
        name: '',
        description: '',
        status: 'planning',
        priority: 'medium',
        domain: '',
        sentry_project_ids: []
    });

    const [repoForm, setRepoForm] = useState({
        name: '',
        repository_url: '',
        repository_name: '',
        sub_project_type: 'backend_api',
        framework: '',
        language: ''
    });

    const [issueForm, setIssueForm] = useState({
        title: '',
        description: '',
        priority: 'medium',
        assignee: '',
        tags: [],
        screenshot: null
    });
    const [screenshotPreview, setScreenshotPreview] = useState(null);

    const API_BASE = window.location.protocol === 'https:' ? '/api/projects' : `http://${window.location.hostname}:8010`;
    const GITHUB_API_BASE = window.location.protocol === 'https:' ? '/api/testing' : `http://${window.location.hostname}:8011`;
    const DNS_API_BASE = window.location.protocol === 'https:' ? '/api/dns' : `http://${window.location.hostname}:8013/api/v1`;
    const ERROR_API_BASE = window.location.protocol === 'https:' ? '/api/errors' : `http://${window.location.hostname}:8014/api/v1`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchProjects();
        fetchAvailableDomains();
        fetchAvailableSentryProjects();
    }, []);

    const fetchProjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/projects`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();
            // Filter out placeholder/test projects
            const realProjects = data.filter(p =>
                !p.name.includes('E-commerce Platform') &&
                !p.name.includes('Test Project') &&
                !p.name.includes('Sample Project')
            );
            setProjects(realProjects);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectDetails = async (projectId) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/projects/${projectId}`);
            if (!response.ok) throw new Error('Failed to fetch project details');
            const data = await response.json();
            setSelectedProject(data);
            if (data.sub_projects && data.sub_projects.length > 0) {
                setSelectedRepo(data.sub_projects[0]);
            }
            // Also fetch documentation and issues for this project
            fetchDocumentation(projectId);
            fetchIssues(projectId);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDocumentation = async (projectId) => {
        try {
            const response = await fetch(`${API_BASE}/projects/${projectId}/documentation`);
            if (!response.ok) throw new Error('Failed to fetch documentation');
            const data = await response.json();
            setDocumentation(data);
        } catch (err) {
            console.error('Error fetching documentation:', err);
            setDocumentation([]);
        }
    };

    const fetchIssues = async (projectId) => {
        try {
            const response = await fetch(`${API_BASE}/projects/${projectId}/issues`);
            if (!response.ok) throw new Error('Failed to fetch issues');
            const data = await response.json();
            setIssues(data);
        } catch (err) {
            console.error('Error fetching issues:', err);
            setIssues([]);
        }
    };

    const fetchAvailableDomains = async () => {
        setLoadingDomains(true);
        try {
            const response = await fetch(`${DNS_API_BASE}/domains`, {
                headers: {
                    'Authorization': `Bearer inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch domains');
            const data = await response.json();
            setAvailableDomains(data.domains || []);
        } catch (err) {
            console.error('Error fetching available domains:', err);
            setAvailableDomains([]);
        } finally {
            setLoadingDomains(false);
        }
    };

    const fetchAvailableSentryProjects = async () => {
        setLoadingSentryProjects(true);
        try {
            const response = await fetch(`${ERROR_API_BASE}/projects`, {
                headers: {
                    'Authorization': `Bearer inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q`
                }
            });
            if (!response.ok) {
                console.log('Sentry projects not available (API might not be configured)');
                setAvailableSentryProjects([]);
                return;
            }
            const data = await response.json();
            setAvailableSentryProjects(data.projects || []);
        } catch (err) {
            console.error('Error fetching Sentry projects:', err);
            setAvailableSentryProjects([]);
        } finally {
            setLoadingSentryProjects(false);
        }
    };

    const handleDocumentClick = async (docId) => {
        try {
            const response = await fetch(`${API_BASE}/documentation/${docId}`);
            if (!response.ok) throw new Error('Failed to fetch document');
            const fullDoc = await response.json();
            setSelectedDoc(fullDoc);
        } catch (err) {
            console.error('Error fetching document:', err);
            setError(`Failed to load document: ${err.message}`);
        }
    };

    const handleScreenshotChange = (file) => {
        if (!file) return;

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setScreenshotPreview(reader.result);
        };
        reader.readAsDataURL(file);

        setIssueForm(prev => ({ ...prev, screenshot: file }));
    };

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                handleScreenshotChange(file);
                e.preventDefault();
                break;
            }
        }
    };

    const uploadScreenshot = async (projectId, file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE}/projects/${projectId}/screenshots`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) throw new Error('Failed to upload screenshot');
        const data = await response.json();
        return data.url;
    };

    const createIssue = async (e, issueType) => {
        e.preventDefault();
        if (!selectedProject) return;

        setLoading(true);
        setError(null);
        try {
            let screenshotUrl = null;

            // Upload screenshot if provided
            if (issueForm.screenshot) {
                screenshotUrl = await uploadScreenshot(selectedProject.id, issueForm.screenshot);
            }

            const issueData = {
                title: issueForm.title,
                description: issueForm.description,
                priority: issueForm.priority,
                assignee: issueForm.assignee,
                tags: issueForm.tags,
                issue_type: issueType,
                metadata: screenshotUrl ? { screenshot: screenshotUrl } : {}
            };

            const response = await fetch(`${API_BASE}/projects/${selectedProject.id}/issues`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(issueData)
            });
            if (!response.ok) throw new Error('Failed to create issue');

            setShowBugModal(false);
            setShowFeatureModal(false);
            setScreenshotPreview(null);
            await fetchIssues(selectedProject.id);
            setIssueForm({ title: '', description: '', priority: 'medium', assignee: '', tags: [], screenshot: null });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const openEditIssue = (issue) => {
        setEditingIssue(issue);
        setIssueForm({
            title: issue.title || '',
            description: issue.description || '',
            priority: issue.priority || 'medium',
            assignee: issue.assignee || issue.reporter || '',
            tags: issue.tags || []
        });
        setShowEditIssueModal(true);
    };

    const updateIssue = async (e) => {
        e.preventDefault();
        if (!editingIssue || !selectedProject) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/issues/${editingIssue.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(issueForm)
            });
            if (!response.ok) throw new Error('Failed to update issue');

            setShowEditIssueModal(false);
            setEditingIssue(null);
            await fetchIssues(selectedProject.id);
            setIssueForm({ title: '', description: '', priority: 'medium', assignee: '', tags: [] });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteIssue = async (issueId) => {
        if (!selectedProject) return;
        if (!window.confirm('Are you sure you want to delete this issue?')) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/issues/${issueId}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Failed to delete issue');

            await fetchIssues(selectedProject.id);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const unlinkRepository = async (repoId, e) => {
        e.stopPropagation(); // Prevent card click
        if (!selectedProject) return;
        if (!window.confirm('Remove this repository from the project?\n\nThis will only remove the link to the repository, not delete it from GitHub.')) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/sub-projects/${repoId}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Failed to unlink repository');

            // Refresh project details
            await fetchProjectDetails(selectedProject.id);
            setSelectedRepo(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const createProject = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectForm)
            });
            if (!response.ok) throw new Error('Failed to create project');
            const newProject = await response.json();
            setShowCreateModal(false);
            await fetchProjects();
            fetchProjectDetails(newProject.id);
            setProjectForm({ name: '', description: '', status: 'planning', priority: 'medium' });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = () => {
        if (!selectedProject) return;
        setEditForm({
            name: selectedProject.name || '',
            description: selectedProject.description || '',
            status: selectedProject.status || 'planning',
            priority: selectedProject.priority || 'medium',
            domain: selectedProject.domain || '',
            sentry_project_ids: selectedProject.sentry_project_ids || []
        });
        setShowEditModal(true);
    };

    const updateProject = async (e) => {
        e.preventDefault();
        if (!selectedProject) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/projects/${selectedProject.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm)
            });
            if (!response.ok) throw new Error('Failed to update project');
            const updatedProject = await response.json();
            setShowEditModal(false);
            setSelectedProject(updatedProject);
            await fetchProjects();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchGithubRepos = async () => {
        setLoadingGithub(true);
        setError(null);
        try {
            const response = await fetch(`${GITHUB_API_BASE}/github/repositories?include_own=true`);
            if (response.ok) {
                const data = await response.json();
                setGithubRepos(data.repositories || []);
            } else {
                const errorText = await response.text();
                if (response.status === 500) {
                    setError('GitHub token not configured. Please configure your GitHub token in the Secrets Vault first.');
                } else if (response.status === 400) {
                    setError('Invalid GitHub API request. Please check your configuration.');
                } else {
                    setError(`Failed to fetch GitHub repositories: ${errorText}`);
                }
            }
        } catch (err) {
            console.error('Failed to fetch GitHub repos:', err);
            setError('Unable to connect to GitHub service. Please ensure it is running.');
        } finally {
            setLoadingGithub(false);
        }
    };

    const toggleGithubRepoSelection = (repo) => {
        setSelectedGithubRepos(prev => {
            const isSelected = prev.some(r => r.id === repo.id);
            if (isSelected) {
                return prev.filter(r => r.id !== repo.id);
            } else {
                return [...prev, repo];
            }
        });
    };

    const addSelectedRepositories = async () => {
        if (!selectedProject || selectedGithubRepos.length === 0) return;

        setLoading(true);
        setError(null);
        try {
            // Add all selected repos
            const addPromises = selectedGithubRepos.map(repo =>
                fetch(`${API_BASE}/projects/${selectedProject.id}/sub-projects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: repo.name,
                        repository_url: repo.html_url,
                        repository_name: repo.full_name,
                        sub_project_type: 'backend_api',
                        framework: repo.topics?.[0] || '',
                        language: repo.language || '',
                        technology_stack: repo.language ? [repo.language] : []
                    })
                })
            );

            const results = await Promise.all(addPromises);
            const failed = results.filter(r => !r.ok);

            if (failed.length > 0) {
                throw new Error(`Failed to add ${failed.length} repositories`);
            }

            setShowAddRepoModal(false);
            await fetchProjectDetails(selectedProject.id);
            setRepoForm({ name: '', repository_url: '', repository_name: '', sub_project_type: 'backend_api', framework: '', language: '' });
            setGithubRepos([]);
            setSelectedGithubRepos([]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const addRepository = async (e) => {
        e.preventDefault();
        if (!selectedProject) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/projects/${selectedProject.id}/sub-projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(repoForm)
            });
            if (!response.ok) throw new Error('Failed to add repository');
            setShowAddRepoModal(false);
            await fetchProjectDetails(selectedProject.id);
            setRepoForm({ name: '', repository_url: '', repository_name: '', sub_project_type: 'backend_api', framework: '', language: '' });
            setGithubRepos([]);
            setSelectedGithubRepos([]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const variant =
            status === 'active' ? 'success' :
            status === 'on_hold' ? 'warning' :
            'info';
        return <Badge variant={variant}>{status}</Badge>;
    };

    const PriorityBadge = ({ priority }) => {
        const variant =
            priority === 'critical' ? 'danger' :
            priority === 'high' ? 'warning' :
            'info';
        return <Badge variant={variant}>{priority}</Badge>;
    };

    const renderProjectsList = () => (
        <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold">Projects</h2>
                <Button
                    onClick={() => setShowCreateModal(true)}
                    variant="primary"
                >
                    + New Project
                </Button>
            </div>

            <div className="divide-y">
                {projects.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <p className="mb-4">No projects yet</p>
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            variant="primary"
                        >
                            Create Your First Project
                        </Button>
                    </div>
                ) : (
                    projects.map(project => (
                        <div
                            key={project.id}
                            onClick={() => fetchProjectDetails(project.id)}
                            className="p-4 hover:bg-gray-50 cursor-pointer"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <h3 className="text-lg font-semibold">{project.name}</h3>
                                        <StatusBadge status={project.status} />
                                        <PriorityBadge priority={project.priority} />
                                    </div>
                                    <p className="text-sm text-gray-600 mb-2">{project.description || 'No description'}</p>
                                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                                        <span><i className="fas fa-folder" style={{ marginRight: '4px', color: '#000000' }}></i>{project.total_sub_projects || 0} repos</span>
                                        <span><i className="fas fa-check-circle" style={{ marginRight: '4px', color: '#000000' }}></i>{project.completion_percentage || 0}% complete</span>
                                        <span><i className="fas fa-calendar" style={{ marginRight: '4px', color: '#000000' }}></i>{new Date(project.created_at).toLocaleDateString()}</span>
                                        {project.domain && (
                                            <span className="flex items-center text-blue-600">
                                                <i className="fas fa-globe" style={{ marginRight: '4px' }}></i>
                                                {project.domain}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const renderRepoCard = (repo) => (
        <div
            key={repo.id}
            onClick={() => setSelectedRepo(repo)}
            className={`p-4 border rounded-lg cursor-pointer hover:border-[#FFC65D] group ${
                selectedRepo?.id === repo.id ? 'border-[#FFC65D] bg-[#FFC65D] bg-opacity-10' : 'bg-white'
            }`}
        >
            <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold">{repo.name}</h4>
                <div className="flex items-center gap-2">
                    <StatusBadge status={repo.status} />
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setConfiguringSubProject(repo);
                            setShowDatabaseConfigModal(true);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Configure database"
                    >
                        <i className="fas fa-database text-sm"></i>
                    </button>
                    <button
                        onClick={(e) => unlinkRepository(repo.id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-orange-600 hover:bg-orange-50 rounded"
                        title="Unlink repository from project"
                    >
                        <i className="fas fa-unlink text-sm"></i>
                    </button>
                </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">{repo.description || 'No description'}</p>
            {repo.repository_url && (
                <a
                    href={repo.repository_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#FFC65D] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    <i className="fas fa-link" style={{ marginRight: '4px', color: '#000000' }}></i>{repo.repository_name || 'View Repository'}
                </a>
            )}
            <div className="mt-2 flex items-center space-x-2 text-sm text-gray-500">
                {repo.framework && <span className="px-2 py-1 bg-gray-100 rounded">{repo.framework}</span>}
                {repo.language && <span className="px-2 py-1 bg-gray-100 rounded">{repo.language}</span>}
            </div>
        </div>
    );

    const renderProjectDetail = () => {
        if (!selectedProject) return null;

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <Button
                                onClick={() => {
                                    setSelectedProject(null);
                                    setSelectedRepo(null);
                                }}
                                variant="ghost"
                            >
                                ← Back to Projects
                            </Button>
                            <div className="flex items-center space-x-3">
                                <h1 className="text-3xl font-bold">{selectedProject.name}</h1>
                                <StatusBadge status={selectedProject.status} />
                                <PriorityBadge priority={selectedProject.priority} />
                            </div>
                            <p className="text-gray-600 mt-2">{selectedProject.description}</p>
                        </div>
                        <Button
                            onClick={openEditModal}
                            variant="secondary"
                        >
                            <i className="fas fa-edit" style={{ marginRight: '8px' }}></i>
                            Edit Project
                        </Button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-gray-50 p-4 rounded">
                            <div className="text-2xl font-bold text-gray-600">{selectedProject.sub_projects?.length || 0}</div>
                            <div className="text-sm text-gray-600">Repositories</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded">
                            <div className="text-2xl font-bold text-green-600">0</div>
                            <div className="text-sm text-green-600">Active Tests</div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded">
                            <div className="text-2xl font-bold text-purple-600">{documentation.length}</div>
                            <div className="text-sm text-purple-600">Documentation</div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded">
                            <div className="text-2xl font-bold text-orange-600">0</div>
                            <div className="text-sm text-orange-600">Open Issues</div>
                        </div>
                    </div>
                </div>

                {/* Project Tabs */}
                <div className="bg-white rounded-lg shadow">
                    <div className="border-b">
                        <div className="flex space-x-8 px-6">
                            {['overview', 'repositories', 'documentation', 'issues', ...(selectedProject.domain ? ['dns-manager'] : []), 'database'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm ${
                                        activeTab === tab
                                            ? 'border-[#FFC65D] text-[#FFC65D]'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab === 'dns-manager' ? 'DNS Manager' : tab === 'database' ? 'Database Schema' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div>
                                <h3 className="text-lg font-semibold mb-4">
                                    <i className="fas fa-info-circle" style={{ marginRight: '8px', color: '#000000' }}></i>
                                    Project Overview
                                </h3>

                                <div className="space-y-6">
                                    {/* Project Details */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Status</label>
                                                <StatusBadge status={selectedProject.status} />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Priority</label>
                                                <PriorityBadge priority={selectedProject.priority} />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Category</label>
                                                <p className="text-gray-900">{selectedProject.category || 'Not specified'}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Created</label>
                                                <p className="text-gray-900">{new Date(selectedProject.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Last Updated</label>
                                                <p className="text-gray-900">{new Date(selectedProject.updated_at).toLocaleDateString()}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 block mb-1">Repositories</label>
                                                <p className="text-gray-900">{selectedProject.sub_projects?.length || 0}</p>
                                            </div>
                                            {selectedProject.domain && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-700 block mb-1">Domain</label>
                                                    <p className="text-gray-900 flex items-center">
                                                        <i className="fas fa-globe mr-2 text-blue-600"></i>
                                                        {selectedProject.domain}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div>
                                        <h4 className="text-md font-semibold mb-3">Quick Actions</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <Button variant="primary" onClick={() => setActiveTab('repositories')}>
                                                <i className="fas fa-box mr-2"></i>
                                                View Repositories
                                            </Button>
                                            <Button variant="secondary" onClick={() => setActiveTab('documentation')}>
                                                <i className="fas fa-file-alt mr-2"></i>
                                                Documentation
                                            </Button>
                                            <Button variant="warning" onClick={() => setActiveTab('issues')}>
                                                <i className="fas fa-exclamation-circle mr-2"></i>
                                                Issues
                                            </Button>
                                            <Button variant="success" onClick={() => setShowAddRepoModal(true)}>
                                                <i className="fas fa-plus mr-2"></i>
                                                Add Repository
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Project Stats Summary */}
                                    <div>
                                        <h4 className="text-md font-semibold mb-3">Summary</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-blue-50 p-4 rounded-lg">
                                                <div className="text-2xl font-bold text-blue-600">{selectedProject.sub_projects?.length || 0}</div>
                                                <div className="text-sm text-blue-600">Repositories</div>
                                            </div>
                                            <div className="bg-purple-50 p-4 rounded-lg">
                                                <div className="text-2xl font-bold text-purple-600">{documentation.length}</div>
                                                <div className="text-sm text-purple-600">Docs</div>
                                            </div>
                                            <div className="bg-red-50 p-4 rounded-lg">
                                                <div className="text-2xl font-bold text-red-600">{issues.filter(i => i.issue_type === 'bug').length}</div>
                                                <div className="text-sm text-red-600">Bugs</div>
                                            </div>
                                            <div className="bg-green-50 p-4 rounded-lg">
                                                <div className="text-2xl font-bold text-green-600">{issues.filter(i => i.issue_type === 'feature_request').length}</div>
                                                <div className="text-sm text-green-600">Features</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Repositories Tab */}
                        {activeTab === 'repositories' && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold"><i className="fas fa-box" style={{ marginRight: '8px', color: '#000000' }}></i>Repositories ({selectedProject.sub_projects?.length || 0})</h2>
                                    <Button
                                        onClick={() => setShowAddRepoModal(true)}
                                        variant="success"
                                    >
                                        + Add Repository
                                    </Button>
                                </div>

                                {selectedProject.sub_projects?.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <p className="mb-4">No repositories linked yet</p>
                                        <Button
                                            onClick={() => setShowAddRepoModal(true)}
                                            variant="success"
                                        >
                                            Link Your First Repository
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-4">
                                            <Input
                                                type="text"
                                                placeholder="Search repositories..."
                                                value={repoSearchQuery}
                                                onChange={(e) => setRepoSearchQuery(e.target.value)}
                                                fullWidth
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {selectedProject.sub_projects
                                                .filter(repo =>
                                                    !repoSearchQuery ||
                                                    repo.name.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                                    repo.repository_name?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                                    repo.language?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                                    repo.framework?.toLowerCase().includes(repoSearchQuery.toLowerCase())
                                                )
                                                .map(repo => renderRepoCard(repo))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Documentation Tab */}
                        {activeTab === 'documentation' && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold"><i className="fas fa-file-alt" style={{ marginRight: '8px', color: '#000000' }}></i>Documentation</h3>
                                </div>

                                {documentation.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No documentation yet for this project</p>
                                ) : (
                                    <div className="space-y-4">
                                        {documentation.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                                                onClick={() => handleDocumentClick(doc.id)}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-lg">{doc.title}</h4>
                                                        {doc.summary && (
                                                            <p className="text-gray-600 mt-1 text-sm">{doc.summary}</p>
                                                        )}
                                                        <div className="flex items-center gap-4 mt-2">
                                                            <span className="text-xs text-gray-500">
                                                                <i className="fas fa-user mr-1"></i>{doc.author}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                <i className="fas fa-calendar mr-1"></i>
                                                                {new Date(doc.created_at).toLocaleDateString()}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                <i className="fas fa-tag mr-1"></i>{doc.doc_type}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                <i className="fas fa-code-branch mr-1"></i>v{doc.version}
                                                            </span>
                                                        </div>
                                                        {doc.tags && doc.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 mt-2">
                                                                {doc.tags.map((tag, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                        doc.status === 'published' ? 'bg-green-100 text-green-800' :
                                                        doc.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {doc.status}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Issues Tab */}
                        {activeTab === 'issues' && (
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Bugs Section */}
                                    <div className="bg-white border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold">
                                                <i className="fas fa-bug" style={{ marginRight: '8px', color: '#dc2626' }}></i>
                                                Bugs ({issues.filter(i => i.issue_type === 'bug').length})
                                            </h3>
                                            <Button variant="danger" onClick={() => setShowBugModal(true)}>
                                                + Report Bug
                                            </Button>
                                        </div>
                                        {issues.filter(i => i.issue_type === 'bug').length > 0 ? (
                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {issues.filter(i => i.issue_type === 'bug').map(issue => (
                                                    <div key={issue.id} className="p-3 bg-gray-50 border rounded-lg hover:shadow-sm transition-shadow group">
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <h4 className="font-semibold text-gray-900 text-sm">{issue.title}</h4>
                                                                {issue.description && (
                                                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{issue.description}</p>
                                                                )}
                                                                {issue.metadata?.screenshot && (
                                                                    <div className="mt-2">
                                                                        <img
                                                                            src={issue.metadata.screenshot}
                                                                            alt="Bug screenshot"
                                                                            className="max-h-32 rounded border cursor-pointer hover:opacity-75"
                                                                            onClick={() => window.open(issue.metadata.screenshot, '_blank')}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center gap-2 mt-2">
                                                                    <Badge variant={issue.priority === 'critical' ? 'danger' : issue.priority === 'high' ? 'warning' : 'info'}>
                                                                        {issue.priority}
                                                                    </Badge>
                                                                    <Badge variant={issue.status === 'open' ? 'warning' : issue.status === 'resolved' ? 'success' : 'secondary'}>
                                                                        {issue.status}
                                                                    </Badge>
                                                                    {issue.reporter && <span className="text-xs text-gray-500">by {issue.reporter}</span>}
                                                                    {issue.metadata?.screenshot && (
                                                                        <span className="text-xs text-gray-500">
                                                                            <i className="fas fa-image"></i>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => openEditIssue(issue)}
                                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                    title="Edit"
                                                                >
                                                                    <i className="fas fa-edit text-sm"></i>
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteIssue(issue.id)}
                                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                                    title="Delete"
                                                                >
                                                                    <i className="fas fa-trash text-sm"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-400 text-center py-8 text-sm">No bugs reported</p>
                                        )}
                                    </div>

                                    {/* Features Section */}
                                    <div className="bg-white border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold">
                                                <i className="fas fa-star" style={{ marginRight: '8px', color: '#059669' }}></i>
                                                Features ({issues.filter(i => i.issue_type === 'feature_request').length})
                                            </h3>
                                            <Button variant="success" onClick={() => setShowFeatureModal(true)}>
                                                + New Feature
                                            </Button>
                                        </div>
                                        {issues.filter(i => i.issue_type === 'feature_request').length > 0 ? (
                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {issues.filter(i => i.issue_type === 'feature_request').map(issue => (
                                                    <div key={issue.id} className="p-3 bg-gray-50 border rounded-lg hover:shadow-sm transition-shadow group">
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <h4 className="font-semibold text-gray-900 text-sm">{issue.title}</h4>
                                                                {issue.description && (
                                                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{issue.description}</p>
                                                                )}
                                                                <div className="flex items-center gap-2 mt-2">
                                                                    <Badge variant={issue.priority === 'critical' ? 'danger' : issue.priority === 'high' ? 'warning' : 'info'}>
                                                                        {issue.priority}
                                                                    </Badge>
                                                                    <Badge variant={issue.status === 'open' ? 'warning' : issue.status === 'resolved' ? 'success' : 'secondary'}>
                                                                        {issue.status}
                                                                    </Badge>
                                                                    {issue.assignee && <span className="text-xs text-gray-500">→ {issue.assignee}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => openEditIssue(issue)}
                                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                    title="Edit"
                                                                >
                                                                    <i className="fas fa-edit text-sm"></i>
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteIssue(issue.id)}
                                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                                    title="Delete"
                                                                >
                                                                    <i className="fas fa-trash text-sm"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-400 text-center py-8 text-sm">No features requested</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* DNS Manager Tab */}
                        {activeTab === 'dns-manager' && selectedProject.domain && (
                            <div>
                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold flex items-center">
                                        <i className="fas fa-globe mr-2 text-blue-600"></i>
                                        DNS Manager - {selectedProject.domain}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Manage DNS records for this project's domain
                                    </p>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="text-sm text-blue-800">
                                        <i className="fas fa-info-circle mr-2"></i>
                                        DNS management for project domains will be available here. Navigate to the DNS Manager service to manage all domains.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Database Schema Tab */}
                        {activeTab === 'database' && (
                            <div className="h-screen -m-6">
                                {selectedRepo ? (
                                    <DatabaseViewer
                                        service={{
                                            url: API_BASE,
                                            name: `${selectedRepo.name} Database`
                                        }}
                                        subProjectId={selectedRepo.id}
                                    />
                                ) : (
                                    <DatabaseViewer
                                        service={{
                                            url: API_BASE,
                                            name: 'Project Management Database'
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Repositories Section - Only show if not already in repositories tab */}
                {activeTab !== 'repositories' && false && (
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold"><i className="fas fa-box" style={{ marginRight: '8px', color: '#000000' }}></i>Repositories ({selectedProject.sub_projects?.length || 0})</h2>
                        <Button
                            onClick={() => setShowAddRepoModal(true)}
                            variant="success"
                        >
                            + Add Repository
                        </Button>
                    </div>

                    {selectedProject.sub_projects?.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="mb-4">No repositories linked yet</p>
                            <Button
                                onClick={() => setShowAddRepoModal(true)}
                                variant="success"
                            >
                                Link Your First Repository
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <Input
                                    type="text"
                                    placeholder="Search repositories..."
                                    value={repoSearchQuery}
                                    onChange={(e) => setRepoSearchQuery(e.target.value)}
                                    fullWidth
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {selectedProject.sub_projects
                                    .filter(repo =>
                                        !repoSearchQuery ||
                                        repo.name.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.repository_name?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.language?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.framework?.toLowerCase().includes(repoSearchQuery.toLowerCase())
                                    )
                                    .map(repo => renderRepoCard(repo))}
                            </div>
                        </>
                    )}
                </div>
                )}

                {/* Repositories Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold"><i className="fas fa-box" style={{ marginRight: '8px', color: '#000000' }}></i>Repositories ({selectedProject.sub_projects?.length || 0})</h2>
                        <Button
                            onClick={() => setShowAddRepoModal(true)}
                            variant="success"
                        >
                            + Add Repository
                        </Button>
                    </div>

                    {selectedProject.sub_projects?.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="mb-4">No repositories linked yet</p>
                            <Button
                                onClick={() => setShowAddRepoModal(true)}
                                variant="success"
                            >
                                Link Your First Repository
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <Input
                                    type="text"
                                    placeholder="Search repositories..."
                                    value={repoSearchQuery}
                                    onChange={(e) => setRepoSearchQuery(e.target.value)}
                                    fullWidth
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {selectedProject.sub_projects
                                    .filter(repo =>
                                        !repoSearchQuery ||
                                        repo.name.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.repository_name?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.language?.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
                                        repo.framework?.toLowerCase().includes(repoSearchQuery.toLowerCase())
                                    )
                                    .map(repo => renderRepoCard(repo))}
                            </div>
                        </>
                    )}
                </div>

                {/* Repository Details */}
                {selectedRepo && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b">
                            <div className="flex space-x-8 px-6">
                                {['overview', 'docs', 'tests'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`py-4 px-2 border-b-2 font-medium text-sm ${
                                            activeTab === tab
                                                ? 'border-[#FFC65D] text-[#FFC65D]'
                                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="p-6">
                            {activeTab === 'overview' && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">{selectedRepo.name} - Overview</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Description</label>
                                            <p className="text-gray-600">{selectedRepo.description || 'No description provided'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Repository URL</label>
                                            <p><a href={selectedRepo.repository_url} target="_blank" rel="noopener noreferrer" className="text-[#FFC65D] hover:underline">{selectedRepo.repository_url}</a></p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Framework</label>
                                                <p className="text-gray-600">{selectedRepo.framework || 'Not specified'}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Language</label>
                                                <p className="text-gray-600">{selectedRepo.language || 'Not specified'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'docs' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold"><i className="fas fa-file-alt" style={{ marginRight: '8px', color: '#000000' }}></i>Documentation</h3>
                                    </div>

                                    {documentation.length === 0 ? (
                                        <p className="text-gray-500 text-center py-8">No documentation yet for this project</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {documentation.map((doc) => (
                                                <div
                                                    key={doc.id}
                                                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => handleDocumentClick(doc.id)}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-gray-900">{doc.title}</h4>
                                                            <div className="flex items-center space-x-3 mt-2 text-sm text-gray-600">
                                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                                                    {doc.doc_type.replace('_', ' ')}
                                                                </span>
                                                                <span className={`px-2 py-1 rounded text-xs ${
                                                                    doc.status === 'published' ? 'bg-green-100 text-green-800' :
                                                                    doc.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                                                                    'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                    {doc.status}
                                                                </span>
                                                                {doc.author && <span>by {doc.author}</span>}
                                                                <span>v{doc.version}</span>
                                                                <span>
                                                                    {new Date(doc.updated_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            {doc.summary && (
                                                                <p className="mt-2 text-sm text-gray-600">{doc.summary}</p>
                                                            )}
                                                            {doc.tags && doc.tags.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                    {doc.tags.map((tag, idx) => (
                                                                        <span key={idx} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <i className="fas fa-chevron-right text-gray-400 ml-4"></i>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'tests' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold"><i className="fas fa-flask" style={{ marginRight: '8px', color: '#000000' }}></i>Tests</h3>
                                        <Button variant="secondary">
                                            Configure Tests
                                        </Button>
                                    </div>
                                    <p className="text-gray-500 text-center py-8">No tests configured yet for this repository</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Create Project Modal - JSX instead of function component to prevent re-mounting
    const createProjectModalContent = showCreateModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full m-4">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Create New Project</h3>
                </div>
                <form onSubmit={createProject} className="p-6 space-y-4">
                        <div>
                            <Input
                                type="text"
                                label="Project Name *"
                                value={projectForm.name}
                                onChange={(e) => setProjectForm(prev => ({...prev, name: e.target.value}))}
                                required
                                fullWidth
                            />
                        </div>
                        <div>
                            <Input
                                type="textarea"
                                label="Description"
                                value={projectForm.description}
                                onChange={(e) => setProjectForm(prev => ({...prev, description: e.target.value}))}
                                rows={3}
                                fullWidth
                            />
                        </div>
                        <div>
                            <Select
                                label="Domain"
                                value={projectForm.domain}
                                onChange={(e) => setProjectForm(prev => ({...prev, domain: e.target.value}))}
                                fullWidth
                                disabled={loadingDomains}
                            >
                                <option value="">-- Select a domain (optional) --</option>
                                {availableDomains.map((domain) => (
                                    <option key={domain.id} value={domain.name}>
                                        {domain.name}
                                    </option>
                                ))}
                            </Select>
                            {loadingDomains && <p className="text-xs text-gray-500 mt-1">Loading domains...</p>}
                            {!loadingDomains && availableDomains.length === 0 && (
                                <p className="text-xs text-gray-500 mt-1">No domains available in DNS Manager</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Sentry Projects
                            </label>
                            {loadingSentryProjects ? (
                                <p className="text-xs text-gray-500">Loading Sentry projects...</p>
                            ) : availableSentryProjects.length === 0 ? (
                                <p className="text-xs text-gray-500">No Sentry projects available (configure Sentry in Error Monitor)</p>
                            ) : (
                                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                                    {availableSentryProjects.map(sentryProject => (
                                        <label key={sentryProject.id} className="flex items-center mb-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={projectForm.sentry_project_ids.includes(sentryProject.id)}
                                                onChange={(e) => {
                                                    const ids = [...projectForm.sentry_project_ids];
                                                    if (e.target.checked) {
                                                        ids.push(sentryProject.id);
                                                    } else {
                                                        const index = ids.indexOf(sentryProject.id);
                                                        if (index > -1) ids.splice(index, 1);
                                                    }
                                                    setProjectForm(prev => ({...prev, sentry_project_ids: ids}));
                                                }}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">
                                                {sentryProject.name}
                                                {sentryProject.platform && (
                                                    <span className="text-xs text-gray-500 ml-2">({sentryProject.platform})</span>
                                                )}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">Optional: Link to Sentry projects for error tracking</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Select
                                    label="Status"
                                    value={projectForm.status}
                                    onChange={(e) => setProjectForm(prev => ({...prev, status: e.target.value}))}
                                    options={[
                                        { value: 'planning', label: 'Planning' },
                                        { value: 'active', label: 'Active' },
                                        { value: 'on_hold', label: 'On Hold' },
                                        { value: 'completed', label: 'Completed' }
                                    ]}
                                    fullWidth
                                />
                            </div>
                            <div>
                                <Select
                                    label="Priority"
                                    value={projectForm.priority}
                                    onChange={(e) => setProjectForm(prev => ({...prev, priority: e.target.value}))}
                                    options={[
                                        { value: 'low', label: 'Low' },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'high', label: 'High' },
                                        { value: 'critical', label: 'Critical' }
                                    ]}
                                    fullWidth
                                />
                            </div>
                        </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            variant="ghost"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            variant="primary"
                        >
                            {loading ? 'Creating...' : 'Create Project'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Edit Project Modal - JSX instead of function component to prevent re-mounting
    const editProjectModalContent = showEditModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full m-4">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Edit Project</h3>
                </div>
                <form onSubmit={updateProject} className="p-6 space-y-4">
                    <div>
                        <Input
                            type="text"
                            label="Project Name *"
                            value={editForm.name}
                            onChange={(e) => setEditForm(prev => ({...prev, name: e.target.value}))}
                            required
                            fullWidth
                        />
                    </div>
                    <div>
                        <Input
                            type="textarea"
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm(prev => ({...prev, description: e.target.value}))}
                            rows={3}
                            fullWidth
                        />
                    </div>
                    <div>
                        <Select
                            label="Domain"
                            value={editForm.domain}
                            onChange={(e) => setEditForm(prev => ({...prev, domain: e.target.value}))}
                            fullWidth
                            disabled={loadingDomains}
                        >
                            <option value="">-- Select a domain (optional) --</option>
                            {availableDomains.map((domain) => (
                                <option key={domain.id} value={domain.name}>
                                    {domain.name}
                                </option>
                            ))}
                        </Select>
                        {loadingDomains && <p className="text-xs text-gray-500 mt-1">Loading domains...</p>}
                        {!loadingDomains && availableDomains.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">No domains available in DNS Manager</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sentry Projects
                        </label>
                        {loadingSentryProjects ? (
                            <p className="text-xs text-gray-500">Loading Sentry projects...</p>
                        ) : availableSentryProjects.length === 0 ? (
                            <p className="text-xs text-gray-500">No Sentry projects available (configure Sentry in Error Monitor)</p>
                        ) : (
                            <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                                {availableSentryProjects.map(sentryProject => (
                                    <label key={sentryProject.id} className="flex items-center mb-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editForm.sentry_project_ids.includes(sentryProject.id)}
                                            onChange={(e) => {
                                                const ids = [...editForm.sentry_project_ids];
                                                if (e.target.checked) {
                                                    ids.push(sentryProject.id);
                                                } else {
                                                    const index = ids.indexOf(sentryProject.id);
                                                    if (index > -1) ids.splice(index, 1);
                                                }
                                                setEditForm(prev => ({...prev, sentry_project_ids: ids}));
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">
                                            {sentryProject.name}
                                            {sentryProject.platform && (
                                                <span className="text-xs text-gray-500 ml-2">({sentryProject.platform})</span>
                                            )}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">Optional: Link to Sentry projects for error tracking</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Select
                                label="Status"
                                value={editForm.status}
                                onChange={(e) => setEditForm(prev => ({...prev, status: e.target.value}))}
                                options={[
                                    { value: 'planning', label: 'Planning' },
                                    { value: 'active', label: 'Active' },
                                    { value: 'on_hold', label: 'On Hold' },
                                    { value: 'completed', label: 'Completed' }
                                ]}
                                fullWidth
                            />
                        </div>
                        <div>
                            <Select
                                label="Priority"
                                value={editForm.priority}
                                onChange={(e) => setEditForm(prev => ({...prev, priority: e.target.value}))}
                                options={[
                                    { value: 'low', label: 'Low' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'high', label: 'High' },
                                    { value: 'critical', label: 'Critical' }
                                ]}
                                fullWidth
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            type="button"
                            onClick={() => setShowEditModal(false)}
                            variant="ghost"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            variant="primary"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Add Repository Modal - JSX instead of function component to prevent re-mounting
    const addRepositoryModalContent = showAddRepoModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full m-4 max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Add Repository</h3>
                </div>
                <form onSubmit={addRepository} className="p-6 space-y-4">
                        {/* GitHub Browser Button */}
                        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium">Browse your GitHub repositories</p>
                                    <p className="text-xs text-gray-500">Select multiple repos to add at once</p>
                                    {selectedGithubRepos.length > 0 && (
                                        <p className="text-xs text-green-600 mt-1">
                                            {selectedGithubRepos.length} repo{selectedGithubRepos.length > 1 ? 's' : ''} selected
                                        </p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    {selectedGithubRepos.length > 0 && (
                                        <Button
                                            type="button"
                                            onClick={addSelectedRepositories}
                                            disabled={loading}
                                            variant="success"
                                        >
                                            {loading ? 'Adding...' : `Add ${selectedGithubRepos.length} Repo${selectedGithubRepos.length > 1 ? 's' : ''}`}
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        onClick={fetchGithubRepos}
                                        disabled={loadingGithub}
                                        variant="primary"
                                    >
                                        {loadingGithub ? 'Loading...' : 'Browse GitHub'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* GitHub Repos List */}
                        {githubRepos.length > 0 && (
                            <div className="mb-4 max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                                <div className="divide-y">
                                    {githubRepos.map((repo) => {
                                        const isSelected = selectedGithubRepos.some(r => r.id === repo.id);
                                        return (
                                            <div
                                                key={repo.id}
                                                onClick={() => toggleGithubRepoSelection(repo)}
                                                className={`p-3 cursor-pointer ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start space-x-3 flex-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleGithubRepoSelection(repo)}
                                                            className="mt-1"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="flex-1">
                                                            <h4 className="text-sm font-medium text-gray-900">{repo.name}</h4>
                                                            <p className="text-xs text-gray-600 mt-1">{repo.description || 'No description'}</p>
                                                            <div className="flex items-center space-x-2 mt-2">
                                                                {repo.language && (
                                                                    <span className="px-2 py-0.5 bg-gray-100 text-xs rounded">{repo.language}</span>
                                                                )}
                                                                {repo.topics && repo.topics.length > 0 && (
                                                                    <span className="px-2 py-0.5 bg-[#FFC65D] bg-opacity-20 text-xs rounded">{repo.topics[0]}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div>
                            <Input
                                type="text"
                                label="Repository Name *"
                                value={repoForm.name}
                                onChange={(e) => setRepoForm(prev => ({...prev, name: e.target.value}))}
                                placeholder="e.g., Frontend App"
                                required
                                fullWidth
                            />
                        </div>
                        <div>
                            <Input
                                type="url"
                                label="GitHub URL *"
                                value={repoForm.repository_url}
                                onChange={(e) => setRepoForm(prev => ({...prev, repository_url: e.target.value}))}
                                placeholder="https://github.com/username/repo"
                                required
                                fullWidth
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Select
                                    label="Type"
                                    value={repoForm.sub_project_type}
                                    onChange={(e) => setRepoForm(prev => ({...prev, sub_project_type: e.target.value}))}
                                    options={[
                                        { value: 'frontend_web', label: 'Frontend Web' },
                                        { value: 'backend_api', label: 'Backend API' },
                                        { value: 'backend_microservice', label: 'Microservice' },
                                        { value: 'database_relational', label: 'Database' },
                                        { value: 'infrastructure_docker', label: 'Infrastructure' }
                                    ]}
                                    fullWidth
                                />
                            </div>
                            <div>
                                <Input
                                    type="text"
                                    label="Framework"
                                    value={repoForm.framework}
                                    onChange={(e) => setRepoForm(prev => ({...prev, framework: e.target.value}))}
                                    placeholder="e.g., React, FastAPI"
                                    fullWidth
                                />
                            </div>
                        </div>
                        <div>
                            <Input
                                type="text"
                                label="Language"
                                value={repoForm.language}
                                onChange={(e) => setRepoForm(prev => ({...prev, language: e.target.value}))}
                                placeholder="e.g., JavaScript, Python"
                                fullWidth
                            />
                        </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            type="button"
                            onClick={() => setShowAddRepoModal(false)}
                            variant="ghost"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            variant="success"
                        >
                            {loading ? 'Adding...' : 'Add Repository'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Bug Report Modal
    const bugModalContent = showBugModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full m-4">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Report a Bug</h3>
                    <button onClick={() => setShowBugModal(false)} className="text-gray-500 hover:text-gray-700">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <form onSubmit={(e) => createIssue(e, 'bug')} className="p-6 space-y-4" onPaste={handlePaste}>
                    <div>
                        <Input
                            type="text"
                            label="Bug Title *"
                            value={issueForm.title}
                            onChange={(e) => setIssueForm(prev => ({...prev, title: e.target.value}))}
                            required
                            fullWidth
                        />
                    </div>
                    <div>
                        <Input
                            type="textarea"
                            label="Description"
                            value={issueForm.description}
                            onChange={(e) => setIssueForm(prev => ({...prev, description: e.target.value}))}
                            rows={4}
                            fullWidth
                        />
                    </div>

                    {/* Screenshot Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Screenshot (optional)
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                            {screenshotPreview ? (
                                <div className="relative">
                                    <img
                                        src={screenshotPreview}
                                        alt="Screenshot preview"
                                        className="max-h-48 mx-auto rounded"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setScreenshotPreview(null);
                                            setIssueForm(prev => ({...prev, screenshot: null}));
                                        }}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <i className="fas fa-times text-xs"></i>
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <i className="fas fa-image text-gray-400 text-3xl mb-2"></i>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Drag & drop, paste (Ctrl+V), or click to upload
                                    </p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleScreenshotChange(e.target.files[0])}
                                        className="hidden"
                                        id="screenshot-upload"
                                    />
                                    <label
                                        htmlFor="screenshot-upload"
                                        className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded cursor-pointer hover:bg-gray-200"
                                    >
                                        Choose File
                                    </label>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            <i className="fas fa-info-circle"></i> Tip: Take a screenshot and press Ctrl+V to paste it here
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Select
                                label="Priority"
                                value={issueForm.priority}
                                onChange={(e) => setIssueForm(prev => ({...prev, priority: e.target.value}))}
                                options={[
                                    { value: 'low', label: 'Low' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'high', label: 'High' },
                                    { value: 'critical', label: 'Critical' }
                                ]}
                                fullWidth
                            />
                        </div>
                        <div>
                            <Input
                                type="text"
                                label="Reporter (optional)"
                                value={issueForm.assignee}
                                onChange={(e) => setIssueForm(prev => ({...prev, assignee: e.target.value}))}
                                fullWidth
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => setShowBugModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" type="submit">
                            {loading ? 'Reporting...' : 'Report Bug'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Feature Request Modal
    const featureModalContent = showFeatureModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full m-4">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Request a Feature</h3>
                    <button onClick={() => setShowFeatureModal(false)} className="text-gray-500 hover:text-gray-700">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <form onSubmit={(e) => createIssue(e, 'feature_request')} className="p-6 space-y-4">
                    <div>
                        <Input
                            type="text"
                            label="Feature Title *"
                            value={issueForm.title}
                            onChange={(e) => setIssueForm(prev => ({...prev, title: e.target.value}))}
                            required
                            fullWidth
                        />
                    </div>
                    <div>
                        <Input
                            type="textarea"
                            label="Description"
                            value={issueForm.description}
                            onChange={(e) => setIssueForm(prev => ({...prev, description: e.target.value}))}
                            rows={4}
                            fullWidth
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Select
                                label="Priority"
                                value={issueForm.priority}
                                onChange={(e) => setIssueForm(prev => ({...prev, priority: e.target.value}))}
                                options={[
                                    { value: 'low', label: 'Low' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'high', label: 'High' },
                                    { value: 'critical', label: 'Critical' }
                                ]}
                                fullWidth
                            />
                        </div>
                        <div>
                            <Input
                                type="text"
                                label="Assignee (optional)"
                                value={issueForm.assignee}
                                onChange={(e) => setIssueForm(prev => ({...prev, assignee: e.target.value}))}
                                fullWidth
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => setShowFeatureModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="success" type="submit">
                            {loading ? 'Creating...' : 'Create Feature'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Edit Issue Modal
    const editIssueModalContent = showEditIssueModal && editingIssue ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full m-4">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                        Edit {editingIssue.issue_type === 'bug' ? 'Bug' : 'Feature'}
                    </h3>
                    <button onClick={() => { setShowEditIssueModal(false); setEditingIssue(null); }} className="text-gray-500 hover:text-gray-700">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <form onSubmit={updateIssue} className="p-6 space-y-4">
                    <div>
                        <Input
                            type="text"
                            label="Title *"
                            value={issueForm.title}
                            onChange={(e) => setIssueForm(prev => ({...prev, title: e.target.value}))}
                            required
                            fullWidth
                        />
                    </div>
                    <div>
                        <Input
                            type="textarea"
                            label="Description"
                            value={issueForm.description}
                            onChange={(e) => setIssueForm(prev => ({...prev, description: e.target.value}))}
                            rows={4}
                            fullWidth
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Select
                                label="Priority"
                                value={issueForm.priority}
                                onChange={(e) => setIssueForm(prev => ({...prev, priority: e.target.value}))}
                                options={[
                                    { value: 'low', label: 'Low' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'high', label: 'High' },
                                    { value: 'critical', label: 'Critical' }
                                ]}
                                fullWidth
                            />
                        </div>
                        <div>
                            <Input
                                type="text"
                                label={editingIssue.issue_type === 'bug' ? 'Reporter (optional)' : 'Assignee (optional)'}
                                value={issueForm.assignee}
                                onChange={(e) => setIssueForm(prev => ({...prev, assignee: e.target.value}))}
                                fullWidth
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => { setShowEditIssueModal(false); setEditingIssue(null); }}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {loading ? 'Updating...' : 'Update'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    ) : null;

    // Documentation Viewer Modal
    const documentationModalContent = selectedDoc ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedDoc(null)}>
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden m-4" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold">{selectedDoc.title}</h2>
                        <div className="flex items-center space-x-3 mt-2 text-sm text-gray-600">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                {selectedDoc.doc_type.replace('_', ' ')}
                            </span>
                            <span>v{selectedDoc.version}</span>
                            {selectedDoc.author && <span>by {selectedDoc.author}</span>}
                        </div>
                    </div>
                    <button onClick={() => setSelectedDoc(null)} className="text-gray-500 hover:text-gray-700">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] bg-white">
                    <div className="prose max-w-none">
                        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800 bg-white border-0">
                            {selectedDoc.content}
                        </pre>
                    </div>
                </div>
            </div>
        </div>
    ) : null;

    // Database Config Modal Content
    const databaseConfigModalContent = showDatabaseConfigModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <DatabaseConfigForm
                    subProject={configuringSubProject}
                    onConfigSaved={(result) => {
                        console.log('Database config saved:', result);
                        setShowDatabaseConfigModal(false);
                        setConfiguringSubProject(null);
                    }}
                    onCancel={() => {
                        setShowDatabaseConfigModal(false);
                        setConfiguringSubProject(null);
                    }}
                />
            </div>
        </div>
    ) : null;

    return (
        <div className="p-6">
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-red-800">{error}</span>
                </div>
            )}

            {!selectedProject ? renderProjectsList() : renderProjectDetail()}

            {createProjectModalContent}
            {editProjectModalContent}
            {addRepositoryModalContent}
            {bugModalContent}
            {featureModalContent}
            {editIssueModalContent}
            {documentationModalContent}
            {databaseConfigModalContent}
        </div>
    );
};

export default ProjectsUnified;
